<template>
  <div :class="wrapClasses">
    <template v-if="type !== 'textarea'">
      <span v-if="icon" :class="[prefixCls + '-icon']" @click="handleIconClick">
        <k-icon :type="icon" color="#666"></k-icon>
      </span>
      <input
        ref="input"
        :id="elementId"
        :class="iptClasses"
        :disabled="disabled"
        :readonly="readonly"
        :placeholder="placeholder"
        :type="showPwd ? (passwordVisible ? 'text' : 'password') : type"
        :name="name"
        :maxlength="maxlength"
        :value="currentValue"
        :spellcheck="spellcheck"
        @keyup.enter="handleEnter"
        @keyup="handleKeyup"
        @keypress="handleKeypress"
        @keydown="handleKeydown"
        @focus="handleFocus"
        @blur="handleBlur"
        @compositionstart="handleComposition"
        @compositionupdate="handleComposition"
        @compositionend="handleComposition"
        @input="handleInput"
        @change="handleChange"
      />
      <span
        v-if="clearable && currentValue && !disabled && !showPwd"
        :class="[prefixCls + '-clear']"
        @click="handleClear"
      >
        <k-icon type="close-circle-fill" color="#999"></k-icon>
      </span>

      <span
        v-if="!disabled && showPwd"
        :class="[prefixCls + '-eye']"
        @click="handlePasswordVisible"
      >
        <k-icon
          :type="passwordVisible ? 'eye' : 'eye-close'"
          color="#999"
        ></k-icon>
      </span>

      <div v-if="button" :class="[prefixCls + '-btn']">
        <k-button :disabled="disabled" @click="handleBtnClick">{{
          button
        }}</k-button>
      </div>
    </template>
    <textarea
      v-else
      ref="textarea"
      :id="elementId"
      :placeholder="placeholder"
      :class="textareaClasses"
      :disabled="disabled"
      :readonly="readonly"
      :rows="rows"
      :name="name"
      :value="currentValue"
      :spellcheck="spellcheck"
      @compositionstart="handleComposition"
      @compositionupdate="handleComposition"
      @compositionend="handleComposition"
      @input="handleInput"
    ></textarea>
  </div>
</template>

<script>
const prefixCls = "k-ipt";

export default {
  name: "k-input",
  props: {
    elementId: {
      type: String
    },
    type: {
      validator(val) {
        return [
          "text",
          "textarea",
          "password",
          "url",
          "email",
          "date",
          "number",
          "tel"
        ].includes(val);
      },
      default: "text"
    },
    value: {
      type: [String, Number],
      default: ""
    },
    showPwd: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    maxlength: {
      type: Number
    },
    name: {
      type: String
    },
    spellcheck: {
      type: Boolean,
      default: false
    },
    disabled: Boolean,
    readonly: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    icon: { type: String, default: "" },
    button: { type: String, default: "" },
    rows: {
      type: Number,
      default: 2
    },
    circle: Boolean
  },
  data() {
    return {
      prefixCls: prefixCls,
      currentValue: this.value,
      isOnComposition: false,
      passwordVisible: false
    };
  },
  computed: {
    wrapClasses() {
      return [
        `${prefixCls}-wrapper`,
        {
          [`${prefixCls}-with-btn`]: !!this.button,
          [`${prefixCls}-circle`]: this.circle
        }
      ];
    },
    iptClasses() {
      return [
        `${prefixCls}`,
        {
          [`${prefixCls}-disabled`]: this.disabled,

          [`${prefixCls}-with-btn`]: !!this.button,
          [`${prefixCls}-with-icon`]: !!this.icon
        }
      ];
    },
    textareaClasses() {
      return [
        `${prefixCls}`,
        {
          [`${prefixCls}-disabled`]: this.disabled
        }
      ];
    }
  },
  methods: {
    handleEnter(event) {
      this.$emit("on-enter", event);
      if (this.button) {
        this.$emit("on-button-click", this.currentValue);
      }
    },
    handleKeydown(event) {
      this.$emit("on-keydown", event);
    },
    handleKeypress(event) {
      this.$emit("on-keypress", event);
    },
    handleKeyup(event) {
      this.$emit("on-keyup", event);
    },
    handleFocus(event) {
      this.$emit("on-focus", event);
    },
    handleBlur(event) {
      this.$emit("on-blur", event);
    },
    handleIconClick(event) {
      if (!this.disabled) {
        this.$emit("on-icon-click", event);
      }
    },
    handleBtnClick(event) {
      this.$emit("on-button-click", event);
    },
    handleComposition(event) {
      if (event.type === "compositionstart") {
        this.isOnComposition = true;
      }
      if (event.type === "compositionend") {
        this.isOnComposition = false;
        this.handleInput(event);
      }
    },
    handleInput(event) {
      // 解决中文输入问题
      if (this.isOnComposition) return;

      let value = event.target.value;

      this.$emit("input", value);
      this.setCurrentValue(value);
      this.$emit("on-change", event);
    },
    handleChange(event) {
      this.$emit("on-input-change", event);
    },
    setCurrentValue(value) {
      if (value === this.currentValue) return;
      this.currentValue = value;
    },
    updateIconOffset() {
      let iptBtn = this.$el.querySelector(".k-ipt-btn");
      let el_clear = this.$el.querySelector(".k-ipt-clear");
      let el_eye = this.$el.querySelector(".k-ipt-eye");
      if (iptBtn) {
        if (el_clear) {
          el_clear.style.transform = `translateX(-${(this.$el,
          iptBtn.offsetWidth)}px)`;
        }
        if (el_eye) {
          el_eye.style.transform = `translateX(-${(this.$el,
          iptBtn.offsetWidth)}px)`;
        }
      }
    },
    handleClear() {
      const e = { target: { value: "" } };
      this.$emit("input", "");
      this.setCurrentValue("");
      this.$emit("on-change", e);
      this.$emit("on-clear");
      this.focus();
    },
    handlePasswordVisible() {
      this.passwordVisible = !this.passwordVisible;
      this.focus();
    },
    focus() {
      if (this.type === "textarea") {
        this.$refs.textarea.focus();
      } else {
        this.$refs.input.focus();
      }
    }
  },
  watch: {
    value(val) {
      this.setCurrentValue(val);
    }
  },
  mounted() {
    this.updateIconOffset();
  },
  updated() {
    this.$nextTick(this.updateIconOffset);
  }
};
</script>
<style lang="less" scoped></style>
